import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { Button } from '@cottage/design-system/internal-tooling/ui'
import SettingsSection from '@/components/SettingsSection'
import SearchableSelect from '@/components/SearchableSelect'
import { usePageAccess } from '@/hooks/usePageAccess'
import { getSettings, putSettings } from '@/services/monthlyPerformance'
import { listCases, type CaseListItem } from '@/services/projections'
import { toast } from '@/store/toastStore'
import { formatApiError } from '@/utils/apiError'
import type { MonthlyPerformanceSettings as Settings } from '@/types/monthlyPerformance'
import { BARE_TRIGGER } from '@/pages/projections/gridClasses'
import { formatDate } from '@/pages/projections/projectionsFormat'
import { HoverHelp } from '@/tableControls'

const CUT_KEYS = ['river_oaks', 'highland_park', 'ga'] as const

const INPUT_CLASS = 'input w-full text-sm'

const SECTION_LABELS: Record<string, string> = {
  scorecard: 'Scorecard',
  store_pnl: 'Store P&L',
  movers: 'Largest movers',
  volume_labor: 'Core Operating Metrics',
  cash_flow: 'Cash Flow',
  ytd: 'Year to date strip',
  vs_projection: 'Actual vs. projection (scorecard line, summary block, commentary paragraph)',
  budget: 'Detailed projection columns in Store P&L (also used in the PDF attachment)',
}

const SECTION_HELPERS: Partial<Record<string, string>> = {
  vs_projection:
    'Adds the scorecard vs-projection line, summary block, and versus-projection commentary paragraph.',
  budget:
    'Adds Store P&L projection and vs projection columns from the projection case you choose below.',
}

function wrapHoverHelp(label: ReactNode, help: string, ariaLabel: string) {
  return (
    <span className={BARE_TRIGGER}>
      <HoverHelp label={label} aria-label={ariaLabel}>
        {help}
      </HoverHelp>
    </span>
  )
}

function SettingsField({
  id,
  label,
  children,
}: {
  id: string
  label: string
  children: ReactNode
}) {
  return (
    <div>
      <label className="text-sm font-medium text-gray-700" htmlFor={id}>
        {label}
      </label>
      <div className="mt-1">{children}</div>
    </div>
  )
}

export default function MonthlyPerformanceSettings() {
  const { canEdit } = usePageAccess('monthly-performance')

  const [settings, setSettings] = useState<Settings | null>(null)
  const [loading, setLoading] = useState(true)
  const [projectionCases, setProjectionCases] = useState<CaseListItem[]>([])

  const loadAll = useCallback(async () => {
    setLoading(true)
    try {
      const [settingsData, casesResult] = await Promise.all([
        getSettings(),
        listCases().catch(() => [] as CaseListItem[]),
      ])
      setSettings({
        ...settingsData,
        budget: settingsData.budget ?? { case_id: null },
      })
      setProjectionCases(casesResult.filter((c) => !c.expired && c.saved_at))
    } catch (error) {
      toast.error(formatApiError(error, 'Failed to load settings'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void loadAll()
  }, [loadAll])

  const saveSettings = async () => {
    if (!settings || !canEdit) return
    try {
      const saved = await putSettings(settings)
      setSettings(saved)
      toast.success('Settings saved')
    } catch (error) {
      toast.error(formatApiError(error, 'Failed to save settings'))
    }
  }

  const caseOptions = useMemo(
    () =>
      projectionCases.map((caseItem) => ({
        id: String(caseItem.id),
        label: caseItem.name,
        caption: caseItem.expires_on
          ? `Expires ${formatDate(caseItem.expires_on)}`
          : undefined,
      })),
    [projectionCases],
  )

  const budgetCaseId = settings?.budget?.case_id ?? null

  if (loading || !settings) {
    return <p className="text-sm text-gray-500">Loading settings…</p>
  }

  return (
    <div className="space-y-6">
      <SettingsSection
        title="Cuts"
        description="Store labels and open dates used for comparisons"
      >
        <div className="space-y-4">
          {CUT_KEYS.map((cutKey) => {
            const cut = settings.cuts[cutKey]
            if (!cut) return null
            return (
              <div key={cutKey} className="grid gap-3 rounded border border-gray-100 p-4 md:grid-cols-2">
                <SettingsField id={`mp-settings-cut-${cutKey}-label`} label="Label">
                  <input
                    id={`mp-settings-cut-${cutKey}-label`}
                    className={INPUT_CLASS}
                    value={cut.label}
                    disabled={!canEdit}
                    onChange={(e) =>
                      setSettings({
                        ...settings,
                        cuts: {
                          ...settings.cuts,
                          [cutKey]: { ...cut, label: e.target.value },
                        },
                      })
                    }
                  />
                </SettingsField>
                {cutKey !== 'ga' ? (
                  <>
                    <SettingsField
                      id={`mp-settings-cut-${cutKey}-short`}
                      label="Store short name"
                    >
                      <input
                        id={`mp-settings-cut-${cutKey}-short`}
                        className={INPUT_CLASS}
                        value={cut.store_name_short ?? ''}
                        disabled={!canEdit}
                        onChange={(e) =>
                          setSettings({
                            ...settings,
                            cuts: {
                              ...settings.cuts,
                              [cutKey]: { ...cut, store_name_short: e.target.value },
                            },
                          })
                        }
                      />
                    </SettingsField>
                    <SettingsField id={`mp-settings-cut-${cutKey}-open`} label="Open date">
                      <input
                        id={`mp-settings-cut-${cutKey}-open`}
                        type="date"
                        className={INPUT_CLASS}
                        value={cut.open_date ?? ''}
                        disabled={!canEdit}
                        onChange={(e) =>
                          setSettings({
                            ...settings,
                            cuts: {
                              ...settings.cuts,
                              [cutKey]: { ...cut, open_date: e.target.value },
                            },
                          })
                        }
                      />
                      {cutKey === 'highland_park' ? (
                        <p className="mt-1 text-xs text-amber-800">
                          Highland Park comparisons start Jan 2026 in the model. Seed open date is
                          2026-01-01.
                        </p>
                      ) : null}
                    </SettingsField>
                  </>
                ) : null}
              </div>
            )
          })}
          <Button variant="primary" disabled={!canEdit} onClick={() => void saveSettings()}>
            Save cuts
          </Button>
        </div>
      </SettingsSection>

      <SettingsSection title="Sections" description="Toggle report sections">
        <div className="mb-4">
          <SettingsField id="mp-settings-budget-case" label="Projection case (default for reports)">
            <SearchableSelect
              id="mp-settings-budget-case"
              options={caseOptions}
              value={budgetCaseId != null ? String(budgetCaseId) : null}
              allowClear
              disabled={!canEdit}
              placeholder="Select saved case"
              className="max-w-md"
              onChange={(id) => {
                if (!settings) return
                const nextId = id ? Number(id) : null
                setSettings({
                  ...settings,
                  budget: { case_id: nextId },
                  sections: {
                    ...settings.sections,
                    budget: nextId != null ? settings.sections.budget : false,
                    vs_projection: nextId != null ? settings.sections.vs_projection : false,
                  },
                })
              }}
            />
          </SettingsField>
        </div>
        <div className="grid gap-2 sm:grid-cols-2">
          {Object.entries(settings.sections).map(([key, enabled]) => {
            if (key === 'budget' || key === 'vs_projection') {
              const checkbox = (
                <label className="flex items-center gap-2 text-sm text-gray-800">
                  <input
                    type="checkbox"
                    checked={enabled}
                    disabled={!canEdit || budgetCaseId == null}
                    onChange={(e) =>
                      setSettings({
                        ...settings,
                        sections: { ...settings.sections, [key]: e.target.checked },
                      })
                    }
                  />
                  {SECTION_LABELS[key] ?? key}
                </label>
              )
              return (
                <div key={key}>
                  {budgetCaseId == null
                    ? wrapHoverHelp(
                        checkbox,
                        'Choose a projection case first',
                        'Projection sections require a projection case',
                      )
                    : checkbox}
                  {SECTION_HELPERS[key] ? (
                    <p className="mt-1 pl-6 text-xs text-gray-500">{SECTION_HELPERS[key]}</p>
                  ) : null}
                </div>
              )
            }
            return (
              <div key={key}>
                <label className="flex items-center gap-2 text-sm text-gray-800">
                  <input
                    type="checkbox"
                    checked={enabled}
                    disabled={!canEdit}
                    onChange={(e) =>
                      setSettings({
                        ...settings,
                        sections: { ...settings.sections, [key]: e.target.checked },
                      })
                    }
                  />
                  {SECTION_LABELS[key] ?? key}
                </label>
                {SECTION_HELPERS[key] ? (
                  <p className="mt-1 pl-6 text-xs text-gray-500">{SECTION_HELPERS[key]}</p>
                ) : null}
              </div>
            )
          })}
        </div>
        <div className="mt-4">
          <Button variant="primary" disabled={!canEdit} onClick={() => void saveSettings()}>
            Save sections
          </Button>
        </div>
      </SettingsSection>

      <SettingsSection title="Movers & checks">
        <div className="grid gap-3 md:grid-cols-3">
          <SettingsField id="mp-settings-movers-min-abs" label="Minimum $ change">
            <input
              id="mp-settings-movers-min-abs"
              className={INPUT_CLASS}
              value={settings.movers.min_abs}
              disabled={!canEdit}
              onChange={(e) =>
                setSettings({
                  ...settings,
                  movers: { ...settings.movers, min_abs: e.target.value },
                })
              }
            />
          </SettingsField>
          <SettingsField id="mp-settings-movers-min-pct" label="Minimum % change">
            <input
              id="mp-settings-movers-min-pct"
              className={INPUT_CLASS}
              value={settings.movers.min_pct}
              disabled={!canEdit}
              onChange={(e) =>
                setSettings({
                  ...settings,
                  movers: { ...settings.movers, min_pct: e.target.value },
                })
              }
            />
          </SettingsField>
          <SettingsField id="mp-settings-movers-max" label="Max movers per comparison">
            <input
              id="mp-settings-movers-max"
              type="number"
              className={`${INPUT_CLASS} input-no-spinner`}
              value={settings.movers.max_per_comparison}
              disabled={!canEdit}
              onChange={(e) =>
                setSettings({
                  ...settings,
                  movers: {
                    ...settings.movers,
                    max_per_comparison: Number(e.target.value),
                  },
                })
              }
            />
          </SettingsField>
        </div>
        <div className="mt-4">
          <Button variant="primary" disabled={!canEdit} onClick={() => void saveSettings()}>
            Save movers & checks
          </Button>
        </div>
      </SettingsSection>

      <SettingsSection title="Commentary">
        <div className="space-y-3">
          <p className="text-sm text-gray-700">
            Commentary model:{' '}
            {settings.commentary.effective_model ??
              settings.commentary.model ??
              'claude-opus-5-5'}{' '}
            (
            {settings.commentary.provider === 'anthropic' ? 'Anthropic' : 'OpenAI'})
          </p>
          <SettingsField id="mp-settings-commentary-model" label="Model override">
            <input
              id="mp-settings-commentary-model"
              className={INPUT_CLASS}
              value={settings.commentary.model ?? ''}
              disabled={!canEdit}
              onChange={(e) =>
                setSettings({
                  ...settings,
                  commentary: {
                    ...settings.commentary,
                    model: e.target.value || null,
                  },
                })
              }
            />
          </SettingsField>
          <SettingsField id="mp-settings-commentary-temperature" label="Commentary temperature">
            <input
              id="mp-settings-commentary-temperature"
              type="number"
              min={0}
              max={1}
              step={0.1}
              className={`${INPUT_CLASS} input-no-spinner`}
              value={settings.commentary.temperature}
              disabled={!canEdit}
              onChange={(e) =>
                setSettings({
                  ...settings,
                  commentary: {
                    ...settings.commentary,
                    temperature: Number(e.target.value),
                  },
                })
              }
            />
          </SettingsField>
          <SettingsField id="mp-settings-commentary-audience" label="Audience note">
            <textarea
              id="mp-settings-commentary-audience"
              className={INPUT_CLASS}
              rows={4}
              value={settings.commentary.audience_note}
              disabled={!canEdit}
              onChange={(e) =>
                setSettings({
                  ...settings,
                  commentary: {
                    ...settings.commentary,
                    audience_note: e.target.value,
                  },
                })
              }
            />
          </SettingsField>
          <SettingsField id="mp-settings-commentary-max" label="Max takeaways 3–6">
            <input
              id="mp-settings-commentary-max"
              type="number"
              min={3}
              max={6}
              className={`${INPUT_CLASS} input-no-spinner`}
              value={settings.commentary.max_takeaways}
              disabled={!canEdit}
              onChange={(e) =>
                setSettings({
                  ...settings,
                  commentary: {
                    ...settings.commentary,
                    max_takeaways: Number(e.target.value),
                  },
                })
              }
            />
          </SettingsField>
        </div>
        <div className="mt-4">
          <Button variant="primary" disabled={!canEdit} onClick={() => void saveSettings()}>
            Save commentary
          </Button>
        </div>
      </SettingsSection>
    </div>
  )
}
